/**
 * MEDICAL DEMO — Reset Demo.
 *
 * Mirrors the automotive demo's reset (same pill shape, same RotateCcw icon,
 * same spinner-while-busy, same reload afterwards) and calls the existing
 * `POST /api/demo-med/reset`. One tap, a short "Demo reset" confirmation, then
 * the current persona reloads to its clean starting state.
 *
 * Two placements, one component:
 *   · `inline`  — inside the medical demo header, beside the persona switches
 *                 (coordinator and head office).
 *   · `floating` — a fixed pill for the PATIENT persona, whose screen is the
 *                 real guest flow and therefore has no header. Gated on the
 *                 demo card id, so it can never appear on a real clinic card.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RotateCcw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = (typeof window !== 'undefined' ? window.location.origin : '');
const MED_DEMO_CARD_PREFIX = '/review-shield/MEDDEMO-';

/** True only on the medical demo patient screen. */
export const isMedDemoPatientPath = (pathname) =>
    (pathname || '').toUpperCase().startsWith(MED_DEMO_CARD_PREFIX.toUpperCase());

export default function MedDemoReset({ variant = 'inline' }) {
    const { t } = useTranslation();
    const [busy, setBusy] = useState(false);

    const reset = async () => {
        if (busy) return;
        setBusy(true);
        try {
            const res = await fetch(`${API}/api/demo-med/reset`, { method: 'POST' });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            toast.success(t('demoMed.resetDone', 'Demo reset'));
            setTimeout(() => window.location.reload(), 700);
        } catch (e) {
            toast.error(t('demoMed.resetFailed', 'Reset did not go through'));
            setBusy(false);
        }
    };

    const button = (
        <button
            onClick={reset}
            disabled={busy}
            aria-label={t('demoMed.resetDemo', 'Reset Demo')}
            title={t('demoMed.resetDemo', 'Reset Demo')}
            data-testid="med-demo-reset"
            className="flex shrink-0 items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs font-semibold text-slate-200 transition-colors hover:border-white/30 hover:text-white disabled:opacity-50"
        >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
            <span>{t('demoMed.resetDemo', 'Reset Demo')}</span>
        </button>
    );

    if (variant === 'inline') return button;

    return (
        <div className="fixed bottom-4 left-1/2 z-50 -translate-x-1/2 rounded-full border border-white/10 bg-slate-950/85 px-2 py-1.5 backdrop-blur"
            data-testid="med-demo-reset-pill">
            {button}
        </div>
    );
}
